import React, { useCallback } from "react";
import { TableCell, TableRow, Tooltip, Box, Badge, IconButton, Checkbox } from "@mui/material";
import PaymentActions from "./common/PaymentActions";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Link } from "react-router-dom";
import { encryptProfile } from "../../utils/profileCrypto";
import GSTAmountFormatter from "./common/GstAmountFormatter";

const BADGE_BASE_STYLE = {
  padding: "4px 8px",
  borderRadius: "4px"
};

const PaymentTableRow = React.memo(function PaymentTableRow({
  item,
  onView,
  convertDate,
  getPlatformNames,
  getStatusColor,
  getStatusText,
  onDelete,
  onApprove,
  onGenerateLink,
  onCopyLink,
  isAdmin,
  showOverdueColumn,
  getDaysOverdue,
  selected,
  onToggleSelect,
  isSuperUser,
  onMarkUnderProcess,
  onRevertToWaiting,
}) {
  const handleViewClick = useCallback(() => {
    onView(item);
  }, [item, onView]);

  const handleToggleSelect = useCallback(() => {
    onToggleSelect(item._id);
  }, [item, onToggleSelect]);

  const daysOverdue = showOverdueColumn ? getDaysOverdue(item.payableDate) : null;

  let encryptionData = {};
  if (item.isCompany && item.company?._id) {
    encryptionData = {
      id: item.company?._id,
      isVen: true
    }
  }
  else {
    encryptionData = {
      id: item.influencer?._id,
      isVen: false
    }
  }

  const token = encryptProfile({ ...encryptionData });

  return (
    <TableRow
      hover
    >
      {/* Select — only super-admins get the bulk-selection toolbar this
          feeds, and only Waiting rows can be queued for processing at all
          (enforced again server-side), so the checkbox itself only renders
          for those — other rows keep the empty cell for column alignment
          but have nothing to click. */}
      {isSuperUser && (
        <TableCell padding="checkbox" align="center">
          {item.status === "W" && (
            <Checkbox
              size="small"
              checked={selected}
              onChange={handleToggleSelect}
            />
          )}
        </TableCell>
      )}

      {/* Date Column */}
      <TableCell>
        <Badge
          style={{
            ...BADGE_BASE_STYLE,
            backgroundColor: "#6c757d",
            color: "white",
            fontSize: 10,
          }}
        >
          {convertDate(item._id)}
        </Badge>
      </TableCell>

      {/* Payment Type (Vendor/Influencer) */}
      <TableCell
        align="center"
      >
        {item?.company && item?.company?.name ? (
          <Badge
            style={{
              ...BADGE_BASE_STYLE,
              backgroundColor: "#dc3545",
              color: "white",
              fontSize: "10px",
              fontWeight: 500
            }}
          >
            VEN
          </Badge>
        ) : (
          <Badge
            style={{
              ...BADGE_BASE_STYLE,
              backgroundColor: "#0D6EFD",
              color: "white",
              fontSize: "10px",
              fontWeight: 500
            }}
          >
            INF
          </Badge>
        )}
      </TableCell>

      {/* Campaign Name */}
      <TableCell
        sx={{
          fontSize: "10px",
          maxWidth: "300px",
          whiteSpace: "wrap",
          overflow: "hidden",
          // width: "300px"
        }}
      >
        <Link
          to={`/payments/campaigns/details/${item.campaign?._id}`}
          target="blank"
        >
          {item.campaign?.name || "-"}
        </Link>
        {/* Clubbed link: name the other campaigns it merged (linkChild is populated by fetch-links). */}
        {[...new Map((item.linkChild || [])
          .filter((c) => c?.campaign?._id && c.campaign._id !== item.campaign?._id)
          .map((c) => [c.campaign._id, c.campaign])).values()]
          .map((camp) => (
            <div key={camp._id}>
              + <Link to={`/payments/campaigns/details/${camp._id}`} target="blank">{camp.name}</Link>
            </div>
          ))}
      </TableCell>

      {/* Platform */}
      <TableCell
        sx={{
          fontSize: "10px",
          maxWidth: "100px",
          whiteSpace: 'wrap'
        }}
      >
        {getPlatformNames(item.platform)}
      </TableCell>

      {/* Influencer/Vendor Name */}
      <TableCell>
        {
          item.isCompany && item.company?.name ?
            <Link
              to={`/profile/${token}`}
              target="blank"
            >
              <Badge
                style={{
                  ...BADGE_BASE_STYLE,
                  backgroundColor: "#dc3545",
                  color: "white",
                  fontSize: "10px",
                  fontWeight: 500
                }}
              >
                {item.company.name || "--"}
              </Badge>
            </Link>
            :
            <Link
              to={`/profile/${token}`}
              target="blank"
            >
              <Badge
                style={{
                  ...BADGE_BASE_STYLE,
                  backgroundColor: "#0D6EFD",
                  color: "white",
                  fontSize: "10px",
                  fontWeight: 500
                }}
              >
                {item.influencer?.username || item.influencer?.name || "--"}
              </Badge>
            </Link>
        }
      </TableCell>

      {/* POC */}
      <TableCell
        sx={{
          fontSize: "10px",
          maxWidth: "150px",
          whiteSpace: "nowrap"
        }}
      >
        {item.createdby?.fullName || "-"}
      </TableCell>

      {/* Total Amount */}
      <TableCell>
        <Badge
          style={{
            ...BADGE_BASE_STYLE,
            backgroundColor: "#198754",
            color: "white",
            fontSize: "10px",
          }}
        >
          ₹{item.cost || "-"}
        </Badge>
      </TableCell>

      {/* GST Amount */}
      <TableCell style={{ fontSize: "10px" }}>
        {/* {
          item.isGst &&
          <Badge
            style={{
              ...BADGE_BASE_STYLE,
              backgroundColor: "#ffc107",
              color: "black",
              fontSize: "10px",
            }}
          >
            ₹{item.gstAmount}
          </Badge>
        } */}
        <GSTAmountFormatter
          cell={item.gstAmount}
          row={item}
        />
      </TableCell>

      {/* Percentage */}
      <TableCell
        sx={{ fontSize: "10px" }}
        align="center"
      >
        {item.percentage || 0}%
      </TableCell>

      {/* Payable Amount */}
      <TableCell
        align="center"
      >
        <Badge
          style={{
            ...BADGE_BASE_STYLE,
            backgroundColor: "#ffc107",
            color: "black",
            fontSize: "10px",
          }}
        >
          ₹{item.payableAmount || "-"}
        </Badge>
      </TableCell>

      {/* Remarks */}
      <TableCell
        sx={{
          fontSize: "10px",
          maxWidth: "150px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        align="center"
      >
        {item.remarks || item.payment_status || "-"}
      </TableCell>

      {/* Status */}
      <TableCell
        align="center"
      >
        <Badge
          style={{
            padding: '4px 12px',
            borderRadius: '12px',
            backgroundColor: getStatusColor(item.status, item.queuedForProcessing),
            color: "white",
            fontSize: "9px",
            textTransform: "uppercase"
          }}
        >
          {getStatusText(item.status, item.queuedForProcessing)}
        </Badge>
      </TableCell>

      {/* Overdue By — only shown while the Overdue filter is active */}
      {showOverdueColumn && (
        <TableCell align="center">
          {daysOverdue == null ? (
            "-"
          ) : (
            <Badge
              style={{
                ...BADGE_BASE_STYLE,
                backgroundColor: daysOverdue > 0 ? "#dc3545" : "#6c757d",
                color: "white",
                fontSize: "10px",
              }}
            >
              {daysOverdue > 0 ? `${daysOverdue}d` : "Due today"}
            </Badge>
          )}
        </TableCell>
      )}

      {/* Action */}
      <TableCell>
        <Box
          sx={{
            display: "flex",
            gap: 0,
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={handleViewClick}
            >
              <VisibilityIcon
                fontSize="10px"
                htmlColor="#3c44bc"
              />
            </IconButton>
          </Tooltip>

          <PaymentActions
            row={item}
            rowIndex={-1}
            onDelete={onDelete}
            onApprove={onApprove}
            onGenerateLink={() => { }}
            onCopyLink={onCopyLink}
            isAdmin={isAdmin}
            isSuperUser={isSuperUser}
            onMarkUnderProcess={onMarkUnderProcess}
            onRevertToWaiting={onRevertToWaiting}
          />
        </Box>
      </TableCell>

      {/* Payment Screenshot */}
      <TableCell
        sx={{
          textAlign: 'center'
        }}
      >
        {
          item?.paymentScreenshot ?
            <a
              href={item.paymentScreenshot.includes("https://") ? item.paymentScreenshot : process.env.REACT_APP_PAYMENT_PORTAL_BASE + item.paymentScreenshot}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={item.paymentScreenshot.includes("https://") ? item.paymentScreenshot : process.env.REACT_APP_PAYMENT_PORTAL_BASE + item.paymentScreenshot}
                alt="Payment Screenshot"
                width={36}
                height={36}
                style={{
                  objectFit: "contain",
                  borderRadius: 18,
                  border: "solid 2px #250b1d",
                  cursor: 'pointer',
                  fontSize: 12
                }}
                loading="lazy"
              />
            </a>
            :
            '-'
        }
      </TableCell>
    </TableRow>
  );
});

export default PaymentTableRow;